import {
  DEMO_CREATIVES,
  formatCompactNumber,
  formatRoas,
} from '@/lib/fixtures/demo-brand'

export type CreativeFormat = 'Video' | 'Image' | 'Text'
export type CreativeStatus = 'Active' | 'Draft' | 'Processing' | 'Failed'
export type CreativeChannel = 'search' | 'social' | 'tv' | 'programmatic' | 'audio' | 'any'

export interface CreativeRow {
  id?: string
  name: string
  channel: CreativeChannel
  format: CreativeFormat
  /** "0:30", "0:15", "0:06" for video. Empty for image/text. */
  duration: string
  impressions?: string
  roas?: string
  status: CreativeStatus
  thumbnailUrl?: string | null
  /** Playable URL for video creatives (assembled_url / asset_url). */
  videoUrl?: string | null
  /** API path to DELETE this creative. Undefined for example/demo rows. */
  deleteUrl?: string
  isExample?: boolean
}

function creativeChannel(channel: string): CreativeChannel {
  if (channel === 'search' || channel === 'social' || channel === 'tv' || channel === 'programmatic' || channel === 'audio') return channel
  return 'any'
}

function creativeFormat(format: string): CreativeFormat {
  if (format === 'Image' || format === 'Text') return format
  return 'Video'
}

export const FALLBACK_CREATIVES: CreativeRow[] = DEMO_CREATIVES.map((creative) => ({
  id: creative.id,
  name: creative.name,
  channel: creativeChannel(creative.channel),
  format: creativeFormat(creative.format),
  duration: creative.duration ?? '',
  impressions: formatCompactNumber(creative.impressions),
  roas: formatRoas(creative.roas),
  status: creative.status === 'Paused' ? 'Draft' : creative.status,
}))

/** Per-channel visual palette for the gradient thumbnails (matches prototype CHIP). */
export interface CreativeChannelPalette {
  label: string
  /** 135deg two-stop gradient for the thumb background. */
  gradient: string
  /** Solid hex used for the ROAS color in card metadata. */
  hex: string
  /** Light tint used for the format tag (.tb) background. */
  tagBg: string
  /** Ink color for the format tag and play-button glyph. */
  ink: string
}

export const CREATIVE_CHANNEL_PALETTE: Record<CreativeChannel, CreativeChannelPalette> = {
  search:       { label: 'Search',       gradient: 'linear-gradient(135deg, #FFE8A3, #FFD167)', hex: '#A8780C', tagBg: '#1A1A1A', ink: '#5C3F00' },
  social:       { label: 'Social',       gradient: 'linear-gradient(135deg, #A6E1FF, #7FB0FF)', hex: '#2D6CDF', tagBg: '#1A1A1A', ink: '#0A3A5C' },
  tv:           { label: 'TV / CTV',     gradient: 'linear-gradient(135deg, #D4C7FF, #B9A6FF)', hex: '#7A5BD6', tagBg: '#1A1A1A', ink: '#2D1B5E' },
  programmatic: { label: 'Programmatic', gradient: 'linear-gradient(135deg, #C7F5DD, #8FE0B5)', hex: '#0A8A6E', tagBg: '#1A1A1A', ink: '#0A3D29' },
  audio:         { label: 'Audio',        gradient: 'linear-gradient(135deg, #FFE0B5, #FFB36B)', hex: '#E0762D', tagBg: '#1A1A1A', ink: '#5C3300' },
  any:           { label: 'Any channel',  gradient: 'linear-gradient(135deg, #E9E5DC, #D6D1C7)', hex: '#6F6658', tagBg: '#1A1A1A', ink: '#3E392F' },
}
